import { useEffect, useImperativeHandle, useRef, type Ref } from 'react'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { labelForToken } from 'app/lib/workflows/dataContext'
import { parseTokenSegments } from 'app/lib/workflows/messageFallbacks'

/** What the picker calls to drop a variable at the caret. */
export type TokenEditorHandle = { insertToken: (key: string) => void }

type TokenEditorProps = {
  /** The engine text — tokens carry their full syntax (`{{contact.firstName ?? "there"}}`) here. */
  value: string
  onChange: (value: string) => void
  placeholder?: string
  readOnly?: boolean
  invalid?: boolean
  /** false = input-like: one line, Enter is ignored, pasted newlines collapse to spaces. */
  multiline?: boolean
  className?: string
  /** Classes for the editable field itself, after its own, e.g. to match a page's input focus. */
  fieldClassName?: string
  /** Chip text for a token; defaults to the workflow field catalog's label. */
  labelFor?: (token: string) => string
  ref?: Ref<TokenEditorHandle>
}

const CHIP_CLASS =
  `mx-px inline-flex -translate-y-px items-center rounded-full border border-blue-200 bg-blue-50 ` +
  `px-1.5 py-px align-middle text-[11px] font-medium whitespace-nowrap text-blue-700 select-all`

function buildChip(doc: Document, raw: string, label: string): HTMLSpanElement {
  const chip = doc.createElement(`span`)
  chip.setAttribute(`data-raw`, raw)
  chip.setAttribute(`contenteditable`, `false`)
  chip.className = CHIP_CLASS
  chip.textContent = label
  return chip
}

/** Rebuild the DOM from the engine text. Only for values changed OUTSIDE this editor (template
 *  picked, fallback edited) — never while typing, which is what keeps the caret stable. */
function renderValue(el: HTMLElement, value: string, label: (token: string) => string) {
  el.replaceChildren(
    ...parseTokenSegments(value).map((segment) =>
      segment.kind === `token`
        ? buildChip(el.ownerDocument, segment.raw, label(segment.token))
        : el.ownerDocument.createTextNode(segment.text),
    ),
  )
}

/** Read the engine text back out of the DOM: chips contribute their full raw token (fallback and
 *  all), text nodes contribute verbatim, and contentEditable's block/BR soup normalizes to \n. */
function serializeDom(el: HTMLElement): string {
  let out = ``
  const walk = (node: Node, topLevelIndex: number) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? ``
      return
    }
    if (!(node instanceof HTMLElement)) return
    const raw = node.getAttribute(`data-raw`)
    if (raw) {
      out += raw
      return
    }
    if (node.tagName === `BR`) {
      out += `\n`
      return
    }
    // A block element that isn't the first child means the browser wrapped a new line in a <div>.
    const isBlock = node.tagName === `DIV` || node.tagName === `P`
    if (isBlock && topLevelIndex > 0) {
      out += `\n`
      // A blank line is `<div><br></div>`: the div's newline is the line, the br only holds it open.
      if (node.childNodes.length === 1 && node.firstChild?.nodeName === `BR`) return
    }
    node.childNodes.forEach((child) => walk(child, -1))
  }
  el.childNodes.forEach((child, index) => walk(child, index))
  // A lone trailing <br> is how contentEditable represents an empty last line — not user content.
  return out.replace(/\n$/, out.endsWith(`\n\n`) ? `\n` : ``)
}

/** Message editor that renders tokens as atomic chips instead of raw `{{...}}` syntax. Chips carry
 *  their full raw token in `data-raw`, so fallbacks survive invisibly and serialization is
 *  lossless; typing never re-renders the DOM, which is what keeps the caret honest. */
export function TokenEditor({
  value,
  onChange,
  placeholder,
  readOnly = false,
  invalid = false,
  multiline = true,
  className,
  fieldClassName,
  labelFor,
  ref: handleRef,
}: TokenEditorProps) {
  const ref = useRef<HTMLDivElement>(null)
  /** The last value THIS editor emitted; external values equal to it must not clobber the DOM. */
  const lastEmitted = useRef<string | null>(null)
  const copy = useListCopy()
  const label = (token: string) => labelFor?.(token) ?? labelForToken(token, copy)

  useEffect(() => {
    const el = ref.current
    if (!el || value === lastEmitted.current) return
    renderValue(el, value, label)
    lastEmitted.current = value
    // A re-render while focused (paste chipification) drops the caret at the start; end is the
    // least surprising place to put it back.
    if (el.ownerDocument.activeElement === el) {
      const range = el.ownerDocument.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      const selection = el.ownerDocument.defaultView?.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
  }, [value, copy])

  const emit = () => {
    const el = ref.current
    if (!el) return
    const raw = serializeDom(el)
    const next = multiline ? raw : raw.replace(/\n+/g, ` `)
    lastEmitted.current = next
    onChange(next)
  }

  useImperativeHandle(handleRef, () => ({
    insertToken: (key: string) => {
      const el = ref.current
      if (!el) return
      const chip = buildChip(el.ownerDocument, `{{${key}}}`, label(key))
      const selection = el.ownerDocument.defaultView?.getSelection()
      const range =
        selection && selection.rangeCount > 0 && el.contains(selection.getRangeAt(0).commonAncestorContainer)
          ? selection.getRangeAt(0)
          : null
      if (range) {
        range.deleteContents()
        range.insertNode(chip)
        range.setStartAfter(chip)
        range.collapse(true)
        selection?.removeAllRanges()
        selection?.addRange(range)
      } else {
        el.appendChild(chip)
      }
      el.focus()
      emit()
    },
  }))

  return (
    <div className={cn(`relative`, className)}>
      {value === `` && placeholder && (
        <span
          className={cn(
            `pointer-events-none absolute left-3 text-sm text-minimal`,
            // `truncate` needs a width to ellipsis against, so it only goes on the bounded branch.
            multiline ? `top-2 right-3` : `top-1/2 max-w-[calc(100%-1.5rem)] -translate-y-1/2 truncate`,
          )}
        >
          {placeholder}
        </span>
      )}
      <div
        ref={ref}
        role="textbox"
        aria-multiline={multiline || undefined}
        aria-invalid={invalid || undefined}
        aria-readonly={readOnly || undefined}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={emit}
        onKeyDown={(event) => {
          // Input-like mode: Enter must not insert a line, same as a native <input>.
          if (!multiline && event.key === `Enter`) event.preventDefault()
        }}
        onPaste={(event) => {
          // Plain text only — pasted HTML would smuggle formatting into an SMS. Re-rendering after
          // the emit lets any {{tokens}} inside the paste become chips (caret lands at the end).
          event.preventDefault()
          const pasted = event.clipboardData.getData(`text/plain`)
          const text = multiline ? pasted : pasted.replace(/\n+/g, ` `)
          const selection = window.getSelection()
          if (!selection || selection.rangeCount === 0) return
          const range = selection.getRangeAt(0)
          range.deleteContents()
          range.insertNode(document.createTextNode(text))
          range.collapse(false)
          // Emit with lastEmitted left stale so the value effect re-renders — that's what turns any
          // {{tokens}} inside the pasted text into chips.
          const el = ref.current
          if (el) {
            lastEmitted.current = null
            onChange(serializeDom(el))
          }
        }}
        className={cn(
          `w-full rounded-md border border-default px-3 text-sm leading-relaxed`,
          readOnly ? `bg-surface-subtle text-subtle` : `bg-surface`,
          `focus:border-blue-500 focus:outline-none`,
          multiline
            ? `min-h-24 py-2 whitespace-pre-wrap`
            : // Input-like: one visual line that scrolls horizontally, no wrapping. The scrollbar
              // stays hidden so it reads as a plain text field.
              `overflow-x-auto py-1.5 whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`,
          invalid && `border-red-400`,
          fieldClassName,
        )}
      />
    </div>
  )
}
